import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { AgentMonitor } from "@/components/AgentMonitor";
import { AgentCredentialDialog } from "@/components/AgentCredentialDialog";
import { Cpu, Plus, Trash2, RotateCcw, Copy, Radio, DoorOpen, Link2, ScanFace, Pencil, Download, WifiOff, HardDrive, Activity, CheckCircle2, XCircle, AlertTriangle, RefreshCw, Building2, Database, ShieldCheck } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const BRAND_LABELS = { topdata: "Topdata", intelbras: "Intelbras", outro: "Outro" };
const TYPE_LABELS = { reader: "Leitor facial", turnstile: "Catraca", legacy: "Legado" };

// Modelos por marca + tipo. Dropdown dinâmico.
const MODELS = {
  intelbras: {
    reader: [
      "SS 3540 MF Bio",
      "SS 3540 MF Face",
      "FR 3010 LFT",
      "FR 3020 F",
      "FR 3025 F",
      "iDFace",
      "iDFlex Pro",
    ],
    turnstile: [],
  },
  topdata: {
    reader: [
      "Fit Bio Face",
      "Trix Face",
      "Katraca Fit Face",
      "Inner Bio Prox",
    ],
    turnstile: [
      "Catraca Fit",
      "Catraca Fit Pro",
      "Catraca Inner Fit",
      "Inner Rep IP",
      "Catraca Katraca Fit",
    ],
  },
  outro: { reader: [], turnstile: [] },
};

const EMPTY_FORM = {
  name: "", brand: "intelbras", device_type: "reader", model: "", ip_address: "", unit_id: "",
  paired_device_id: "", release_protocol: "none", release_port: 9999,
  release_endpoint: "", release_command: "01+00+6]00]",
};

export default function Devices() {
  const [devices, setDevices] = useState([]);
  const [units, setUnits] = useState([]);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [tokenDialog, setTokenDialog] = useState(null);
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);

  const load = async () => {
    try {
      const [d, u] = await Promise.all([api.get("/devices"), api.get("/units")]);
      setDevices(d.data); setUnits(u.data);
    } catch { toast.error("Sem permissão"); }
  };

  const loadStatus = async () => {
    setStatusLoading(true);
    try {
      const { data } = await api.get("/devices/status");
      setStatus(data);
    } catch { /* ignore */ } finally { setStatusLoading(false); }
  };

  useEffect(() => {
    load(); loadStatus();
    const t = setInterval(loadStatus, 15000); // auto-refresh 15s
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, []);

  const turnstiles = useMemo(() => devices.filter(d => d.device_type === "turnstile"), [devices]);
  const modelOptions = (MODELS[form.brand] || {})[form.device_type] || [];

  const openNew = () => { setEditingId(null); setForm(EMPTY_FORM); setOpen(true); };
  const openEdit = (d) => {
    setEditingId(d.id);
    setForm({
      name: d.name || "",
      brand: d.brand || "intelbras",
      device_type: d.device_type || "legacy",
      model: d.model || "",
      ip_address: d.ip_address || "",
      unit_id: d.unit_id || "",
      paired_device_id: d.paired_device_id || "",
      release_protocol: d.release_protocol || "none",
      release_port: d.release_port || 9999,
      release_endpoint: d.release_endpoint || "",
      release_command: d.release_command || "01+00+6]00]",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) return toast.error("Nome obrigatório");
    try {
      const body = { ...form };
      if (!body.unit_id) delete body.unit_id;
      if (!body.paired_device_id) delete body.paired_device_id;
      body.release_port = parseInt(body.release_port) || 9999;
      if (editingId) {
        await api.patch(`/devices/${editingId}`, body);
        toast.success("Dispositivo atualizado");
      } else {
        await api.post("/devices", body);
        toast.success("Dispositivo cadastrado");
      }
      setOpen(false); setEditingId(null); setForm(EMPTY_FORM);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const rotate = async (id) => {
    if (!window.confirm("Girar token? O antigo deixa de funcionar imediatamente.")) return;
    const { data } = await api.post(`/devices/${id}/rotate-token`);
    toast.success("Novo token gerado");
    load();
    setTokenDialog({ token: data.api_token, device: devices.find(d => d.id === id) });
  };

  const [credential, setCredential] = useState(null);
  const agentCredential = async (d) => {
    if (!window.confirm(`Gerar credencial do Agente Local para "${d.name}"? Uma credencial anterior deste dispositivo deixa de funcionar.`)) return;
    try { const { data } = await api.post(`/devices/${d.id}/agent-credential`); setCredential(data); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro ao gerar credencial"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remover dispositivo?")) return;
    await api.delete(`/devices/${id}`); toast.success("Removido"); load();
  };

  const testRelease = async (d) => {
    toast.loading(`Testando liberação em "${d.name}"...`, { id: "test-release" });
    try {
      const { data } = await api.post(`/devices/${d.id}/test-release`);
      toast.dismiss("test-release");
      if (data.ok) {
        toast.success(`Catraca liberada! ${data.protocol || ""} ${data.response ? `(resp: ${data.response})` : ""}`);
      } else {
        toast.error(`Falha: ${data.reason || "ver logs"}`);
      }
    } catch (e) { toast.dismiss("test-release"); toast.error(e.response?.data?.detail || "Erro"); }
  };

  const unitName = (id) => units.find(u => u.id === id)?.name || "—";
  const deviceName = (id) => devices.find(d => d.id === id)?.name || "—";
  const copy = (t) => { navigator.clipboard.writeText(t); toast.success("Copiado"); };

  return (
    <div className="space-y-6">
      <AgentMonitor/>
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-extrabold text-slate-900">Dispositivos / Catracas</h1>
          <p className="text-slate-500">Leitor facial Intelbras Bio-T ↔ Catraca TopData (SDK EasyInner via Agente Local v2)</p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditingId(null); setForm(EMPTY_FORM); } }}>
          <DialogTrigger asChild>
            <Button onClick={openNew} data-testid="device-new-button" className="bg-rose-600 hover:bg-rose-700"><Plus className="w-4 h-4 mr-2"/>Novo dispositivo</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editingId ? "Editar dispositivo" : "Novo dispositivo"}</DialogTitle></DialogHeader>
            <div className="space-y-3 mt-4">
              <div><Label>Nome *</Label><Input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} placeholder="Catraca Entrada / Leitor Recepção" data-testid="device-name-input"/></div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Tipo</Label>
                  <Select value={form.device_type} onValueChange={(v)=>setForm({...form,device_type:v, model:""})}>
                    <SelectTrigger data-testid="device-type-select"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="reader">Leitor facial</SelectItem>
                      <SelectItem value="turnstile">Catraca</SelectItem>
                      <SelectItem value="legacy">Legado (webhook)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Fabricante</Label>
                  <Select value={form.brand} onValueChange={(v)=>setForm({...form,brand:v, model:""})}>
                    <SelectTrigger data-testid="device-brand-select"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="intelbras">Intelbras</SelectItem>
                      <SelectItem value="topdata">Topdata</SelectItem>
                      <SelectItem value="outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label>Modelo</Label>
                {modelOptions.length > 0 ? (
                  <Select value={form.model} onValueChange={(v)=>setForm({...form,model:v})}>
                    <SelectTrigger data-testid="device-model-select"><SelectValue placeholder="Selecione o modelo"/></SelectTrigger>
                    <SelectContent>
                      {modelOptions.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      <SelectItem value="__custom__">Outro / Digitar manualmente</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={form.model} onChange={(e)=>setForm({...form,model:e.target.value})} placeholder="Ex: Modelo customizado"/>
                )}
                {form.model === "__custom__" && (
                  <Input className="mt-2" placeholder="Digite o modelo" onChange={(e)=>setForm({...form, model: e.target.value})} value=""/>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div><Label>IP local</Label><Input value={form.ip_address} onChange={(e)=>setForm({...form,ip_address:e.target.value})} placeholder="192.168.1.50" data-testid="device-ip-input"/></div>
                <div>
                  <Label>Unidade</Label>
                  <Select value={form.unit_id} onValueChange={(v)=>setForm({...form,unit_id:v})}>
                    <SelectTrigger><SelectValue placeholder="Selecionar"/></SelectTrigger>
                    <SelectContent>{units.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>

              {form.device_type === "reader" && (
                <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 space-y-2">
                  <Label className="flex items-center gap-2 text-slate-700"><Link2 className="w-4 h-4"/>Catraca vinculada (abre quando este leitor liberar)</Label>
                  <Select value={form.paired_device_id} onValueChange={(v)=>setForm({...form,paired_device_id:v})}>
                    <SelectTrigger data-testid="device-paired-select"><SelectValue placeholder="Nenhuma (apenas registra o evento)"/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">Nenhuma (apenas registra evento)</SelectItem>
                      {turnstiles.map(t => <SelectItem key={t.id} value={t.id}>{t.name} ({t.model || BRAND_LABELS[t.brand]})</SelectItem>)}
                    </SelectContent>
                  </Select>
                  {turnstiles.length === 0 && <p className="text-xs text-slate-500">Cadastre primeiro uma catraca (tipo "Catraca") para habilitar o pareamento.</p>}
                </div>
              )}

              {form.device_type === "turnstile" && (
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200 space-y-3">
                  <Label className="flex items-center gap-2 text-emerald-800"><DoorOpen className="w-4 h-4"/>Protocolo de liberação</Label>
                  <Select value={form.release_protocol} onValueChange={(v)=>setForm({...form,release_protocol:v})}>
                    <SelectTrigger data-testid="device-protocol-select"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="agent_easyinner">TopData EasyInner — Agente local (porta 3570)</SelectItem>
                      {form.release_protocol === "topdata_tcp" && <SelectItem value="topdata_tcp">Topdata Inner Rep (antigo, desativado)</SelectItem>}
                      <SelectItem value="http">HTTP GET (relé genérico)</SelectItem>
                      <SelectItem value="none">Nenhum (só registra o evento)</SelectItem>
                    </SelectContent>
                  </Select>

                  {form.release_protocol === "agent_easyinner" && (
                    <p className="text-[11px] text-emerald-700" data-testid="device-easyinner-note">A catraca conecta no computador da academia (porta 3570) e é controlada só pelo Agente Local. A nuvem nunca envia liberação. Depois de salvar, use "Credencial" na lista para configurar o agente.</p>
                  )}
                  {form.release_protocol === "topdata_tcp" && (
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs">Porta TCP</Label>
                        <Input type="number" value={form.release_port} onChange={(e)=>setForm({...form,release_port:e.target.value})} placeholder="9999" data-testid="device-port-input"/>
                      </div>
                      <div>
                        <Label className="text-xs">Comando (Inner Rep)</Label>
                        <Input value={form.release_command} onChange={(e)=>setForm({...form,release_command:e.target.value})} placeholder="01+00+6]00]" data-testid="device-cmd-input"/>
                      </div>
                      <p className="text-[11px] text-emerald-700 col-span-2">Padrão Inner Rep: STX + <code>{form.release_command}</code> + ETX enviado por socket para <code>{form.ip_address || "<IP>"}:{form.release_port}</code>.</p>
                    </div>
                  )}
                  {form.release_protocol === "http" && (
                    <div>
                      <Label className="text-xs">URL de liberação (HTTP GET)</Label>
                      <Input value={form.release_endpoint} onChange={(e)=>setForm({...form,release_endpoint:e.target.value})} placeholder="http://192.168.1.60/relay/on?duration=1" data-testid="device-url-input"/>
                    </div>
                  )}
                </div>
              )}

              <Button onClick={save} className="w-full bg-rose-600 hover:bg-rose-700" data-testid="device-save-button">
                {editingId ? "Salvar alterações" : "Cadastrar"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="p-5 border-2 border-slate-200 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 text-white relative overflow-hidden" data-testid="status-live-card">
        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl -z-0"/>
        <div className="relative z-10">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div>
              <h3 className="font-heading text-xl font-black flex items-center gap-2 uppercase tracking-wide">
                <Activity className="w-6 h-6 text-emerald-400 animate-pulse"/>Status Ao Vivo
              </h3>
              <p className="text-xs text-slate-400 mt-0.5">
                Atualiza a cada 15s · {status?.server_time ? new Date(status.server_time).toLocaleTimeString("pt-BR") : "—"}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={loadStatus} disabled={statusLoading} className="bg-white/10 border-white/20 text-white hover:bg-white/20" data-testid="status-refresh-btn">
              <RefreshCw className={`w-3.5 h-3.5 mr-2 ${statusLoading ? "animate-spin" : ""}`}/>Atualizar
            </Button>
          </div>

          {!status && (
            <div className="text-center text-slate-400 py-6 text-sm">Carregando status...</div>
          )}

          {status && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4" data-testid="status-totals">
                <StatCard label="Dispositivos" value={status.totals.devices} icon={Cpu} color="slate"/>
                <StatCard label="Online" value={status.totals.online} icon={CheckCircle2} color="emerald" testid="status-total-online"/>
                <StatCard label="Offline" value={status.totals.offline} icon={XCircle} color="rose" testid="status-total-offline"/>
                <StatCard label="Logs p/ sincronizar" value={status.totals.pending_sync} icon={WifiOff} color="amber"/>
              </div>

              {status.units.length === 0 && (
                <div className="text-center text-slate-400 py-6 text-sm">Nenhum dispositivo cadastrado.</div>
              )}

              <div className="space-y-3" data-testid="status-units">
                {status.units.map(u => (
                  <div key={u.unit_id || "none"} className="rounded-xl bg-white/5 border border-white/10 p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
                      <div className="font-heading text-base font-black uppercase tracking-wide">{u.unit_name}</div>
                      <div className="flex items-center gap-3 text-xs">
                        <span className="flex items-center gap-1" title="Agentes/Leitores online">
                          <ScanFace className="w-3.5 h-3.5"/>
                          <strong className={u.agents_online === u.agents_total && u.agents_total > 0 ? "text-emerald-400" : "text-amber-400"}>
                            {u.agents_online}/{u.agents_total}
                          </strong>
                        </span>
                        <span className="flex items-center gap-1" title="Catracas online">
                          <DoorOpen className="w-3.5 h-3.5"/>
                          <strong className={u.turnstiles_online === u.turnstiles_total && u.turnstiles_total > 0 ? "text-emerald-400" : "text-amber-400"}>
                            {u.turnstiles_online}/{u.turnstiles_total}
                          </strong>
                        </span>
                        {u.total_pending_sync > 0 && (
                          <span className="flex items-center gap-1 text-amber-400" title="Logs pendentes de sync">
                            <WifiOff className="w-3.5 h-3.5"/><strong>{u.total_pending_sync}</strong>
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {u.devices.map(d => <DeviceStatusTile key={d.id} d={d}/>)}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </Card>

      <Card className="p-5 border-2 border-sky-200 bg-gradient-to-br from-sky-50 via-white to-sky-50/40" data-testid="face-db-shared-banner">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-2xl bg-sky-600 flex items-center justify-center shadow-lg shadow-sky-200">
            <Database className="w-7 h-7 text-white"/>
          </div>
          <div className="flex-1 min-w-[260px]">
            <h3 className="font-heading text-xl font-black text-slate-900 flex items-center gap-2">
              Banco de Faciais compartilhado <ShieldCheck className="w-5 h-5 text-sky-600"/>
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              <strong>Todas as catracas de todas as unidades</strong> recebem automaticamente o mesmo banco de faciais da nuvem.
              Cadastrou um aluno em uma unidade? Ele já abre a catraca em qualquer outra unidade da rede — sem precisar recadastrar o rosto.
            </p>
            <div className="mt-3 grid sm:grid-cols-3 gap-2 text-xs">
              <div className="rounded-lg bg-white border border-sky-200 p-2.5">
                <div className="font-bold text-sky-700 uppercase tracking-wide text-[10px]">1. Cadastro</div>
                <div className="text-slate-600 mt-0.5">Rosto é salvo na nuvem com descritor 128D</div>
              </div>
              <div className="rounded-lg bg-white border border-sky-200 p-2.5">
                <div className="font-bold text-sky-700 uppercase tracking-wide text-[10px]">2. Sync automático</div>
                <div className="text-slate-600 mt-0.5">Cada agente puxa a base global a cada 60s</div>
              </div>
              <div className="rounded-lg bg-white border border-sky-200 p-2.5">
                <div className="font-bold text-sky-700 uppercase tracking-wide text-[10px]">3. Acesso em qualquer unidade</div>
                <div className="text-slate-600 mt-0.5">Match local no SQLite + abertura TCP da catraca</div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-5 border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 via-white to-emerald-50/40" data-testid="agent-download-card">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-2xl bg-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-200">
            <WifiOff className="w-7 h-7 text-white"/>
          </div>
          <div className="flex-1 min-w-[260px]">
            <h3 className="font-heading text-xl font-black text-slate-900 flex items-center gap-2">
              Agente Local por Unidade <HardDrive className="w-5 h-5 text-emerald-600"/>
            </h3>
            <p className="text-sm text-slate-600 mt-1">
              Cada unidade tem seu próprio PC rodando o Agente Local — ele atende somente as catracas <strong>daquela unidade</strong> (operação 100% offline).
              O banco de faciais, porém, é <strong>o mesmo em todas as unidades</strong> (sincronizado pela nuvem). Baixe o instalador abaixo e rode no PC da recepção de cada unidade.
            </p>

            {units.length === 0 && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900">
                <AlertTriangle className="inline w-4 h-4 mr-1"/>Nenhuma unidade cadastrada. Vá em <strong>Configurações → Unidades</strong> para criar as unidades da sua rede antes de instalar as catracas.
              </div>
            )}

            <div className="mt-4 space-y-3" data-testid="agent-install-by-unit">
              {units.map(u => {
                const unitDevices = devices.filter(d => d.unit_id === u.id);
                const unitTurnstiles = unitDevices.filter(d => d.device_type === "turnstile");
                const unitReaders = unitDevices.filter(d => d.device_type === "reader");
                return (
                  <div key={u.id} className="rounded-xl border-2 border-emerald-200 bg-white p-4" data-testid={`agent-install-unit-${u.id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-3">
                      <div className="flex items-center gap-3">
                        <Building2 className="w-6 h-6 text-emerald-700"/>
                        <div>
                          <div className="font-heading font-black uppercase text-slate-900">{u.name}</div>
                          <div className="text-xs text-slate-500">
                            {unitReaders.length} leitor(es) · {unitTurnstiles.length} catraca(s) nesta unidade
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <a href={`${BACKEND}/americanfit-agent-v2.zip`} target="_blank" rel="noreferrer" data-testid={`agent-download-windows-${u.id}`}>
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 font-bold">
                            <Download className="w-4 h-4 mr-2"/>Agente Local v2 (código + instalador)
                          </Button>
                        </a>
                      </div>
                    </div>
                    <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg text-xs text-slate-700">
                      <strong className="text-slate-900">Passo a passo da instalação nesta unidade:</strong>
                      <ol className="list-decimal list-inside mt-1 space-y-0.5">
                        <li>Cadastre a <strong>catraca</strong> e o <strong>leitor facial</strong> abaixo (botão "Novo dispositivo") selecionando <code className="bg-white px-1 rounded">Unidade: {u.name}</code></li>
                        <li>No PC desta unidade, baixe o ZIP acima e rode <code className="bg-white px-1 rounded">windows\install.bat</code> como administrador</li>
                        <li>Abra o atalho <strong>"FitPass Config"</strong> na Área de trabalho e cole o <strong>token</strong> da catraca (botão "URLs" na tabela abaixo)</li>
                        <li>Pronto — o agente passa a receber os eventos do leitor local e abrir a catraca em TCP, mesmo com internet caída</li>
                      </ol>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="mt-4 p-2.5 bg-rose-50 border border-rose-200 rounded-md text-xs text-rose-800">
              ⚠️ A catraca <strong>não depende</strong> deste PC. Alunos já sincronizados no banco interno do leitor Intelbras/Topdata continuam liberando mesmo com o PC desligado. O agente serve para sync entre a nuvem (banco global) e a catraca local, contingência e logs.
            </div>

            <details className="mt-3 text-xs text-slate-600">
              <summary className="cursor-pointer font-semibold text-slate-700 hover:text-emerald-700">Como funciona o fluxo offline + sync global</summary>
              <ol className="list-decimal list-inside mt-2 space-y-1 pl-2">
                <li>Leitor facial reconhece o aluno e faz POST em <code>http://IP-DO-PC:8765/webhook</code></li>
                <li>Agente valida CPF/face no SQLite local (não depende de internet)</li>
                <li>Agente abre socket TCP para a catraca Topdata (porta 9999) e manda o comando de liberação</li>
                <li>Log de acesso fica em <code>pushed=0</code> no SQLite</li>
                <li>Loop de sync (padrão 60s) envia logs para a nuvem + puxa a <strong>base global de faciais</strong> (todas as unidades)</li>
                <li>Cadastro offline novo: <code>POST /enroll-offline</code> entra na fila para subir ao próximo ciclo</li>
              </ol>
            </details>
          </div>
        </div>
      </Card>

      <Card className="p-5 border border-slate-200 bg-gradient-to-br from-slate-50 to-white">
        <h3 className="font-heading text-lg font-bold text-slate-900 mb-2">Como integrar o hardware</h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-slate-700">
          <div className="space-y-1">
            <div className="font-bold text-slate-900 flex items-center gap-2"><ScanFace className="w-4 h-4 text-emerald-600"/>Leitor Facial Intelbras Linha 3000 (ou Topdata)</div>
            <p>1. No painel do leitor, ative <strong>HTTP Push / Event Notification</strong></p>
            <p>2. Cole a URL do webhook (botão "Ver URLs" na linha do leitor)</p>
            <p>3. Vincule o leitor à catraca que ele deve abrir</p>
          </div>
          <div className="space-y-1">
            <div className="font-bold text-slate-900 flex items-center gap-2"><DoorOpen className="w-4 h-4 text-rose-600"/>Catraca Topdata (Inner Rep TCP)</div>
            <p>1. Habilite "Inner Rep IP" na catraca e anote o <strong>IP + porta</strong> (padrão <code>9999</code>)</p>
            <p>2. Marque o protocolo <strong>Topdata TCP</strong> ao cadastrar</p>
            <p>3. Use o botão <strong>Testar liberação</strong> para validar antes de parear</p>
          </div>
        </div>
      </Card>

      {(() => {
        // Agrupa dispositivos por unidade para visualização clara "cada unidade tem suas catracas"
        const grouped = units.map(u => ({
          unit: u,
          items: devices.filter(d => d.unit_id === u.id),
        }));
        const orphans = devices.filter(d => !d.unit_id || !units.find(u => u.id === d.unit_id));
        if (grouped.length === 0 && orphans.length === 0) {
          return (
            <Card className="border border-slate-200 p-10 text-center text-slate-500" data-testid="devices-empty">
              Nenhum dispositivo cadastrado
            </Card>
          );
        }
        return (
          <div className="space-y-5" data-testid="devices-by-unit">
            {grouped.map(({ unit, items }) => (
              <Card key={unit.id} className="border border-slate-200 overflow-hidden" data-testid={`devices-group-${unit.id}`}>
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-slate-600"/>
                    <div className="font-heading font-black uppercase text-slate-900">{unit.name}</div>
                    <span className="text-xs text-slate-500">({items.length} dispositivo(s))</span>
                  </div>
                  <span className="text-[10px] uppercase tracking-widest bg-sky-100 text-sky-800 px-2 py-0.5 rounded-full font-bold flex items-center gap-1">
                    <Database className="w-3 h-3"/>Banco de faciais global
                  </span>
                </div>
                {items.length === 0 ? (
                  <div className="p-6 text-center text-sm text-slate-400">
                    Nenhuma catraca ou leitor nesta unidade ainda. Clique em "Novo dispositivo" acima.
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[1000px]">
                      <thead className="bg-white border-b border-slate-200">
                        <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                          <th className="px-4 py-3">Dispositivo</th>
                          <th className="px-4 py-3">Tipo / Fabricante</th>
                          <th className="px-4 py-3">IP</th>
                          <th className="px-4 py-3">Pareamento / Protocolo</th>
                          <th className="px-4 py-3">Últ. atividade</th>
                          <th className="px-4 py-3">Status</th>
                          <th className="px-4 py-3"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map(d => renderDeviceRow(d, { TYPE_LABELS, BRAND_LABELS, deviceName, testRelease, setTokenDialog, openEdit, rotate, remove, agentCredential }))}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            ))}
            {orphans.length > 0 && (
              <Card className="border-2 border-dashed border-amber-300 overflow-hidden" data-testid="devices-group-orphans">
                <div className="px-4 py-3 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600"/>
                  <div className="font-heading font-black uppercase text-amber-900">Sem unidade atribuída</div>
                  <span className="text-xs text-amber-700">({orphans.length} dispositivo(s)) — edite para vincular a uma unidade</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1000px]">
                    <thead className="bg-white border-b border-slate-200">
                      <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                        <th className="px-4 py-3">Dispositivo</th>
                        <th className="px-4 py-3">Tipo / Fabricante</th>
                        <th className="px-4 py-3">IP</th>
                        <th className="px-4 py-3">Pareamento / Protocolo</th>
                        <th className="px-4 py-3">Últ. atividade</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {orphans.map(d => renderDeviceRow(d, { TYPE_LABELS, BRAND_LABELS, deviceName, testRelease, setTokenDialog, openEdit, rotate, remove, agentCredential }))}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </div>
        );
      })()}

      <AgentCredentialDialog data={credential} onClose={() => setCredential(null)} copy={copy}/>
      <Dialog open={!!tokenDialog} onOpenChange={(v)=>{if(!v) setTokenDialog(null);}}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Integração — {tokenDialog?.device.name}</DialogTitle></DialogHeader>
          {tokenDialog && (
            <div className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Token do dispositivo</Label>
                <div className="flex gap-2 mt-1">
                  <Input readOnly value={tokenDialog.token} className="font-mono text-xs" data-testid="device-token-display"/>
                  <Button size="icon" variant="outline" onClick={()=>copy(tokenDialog.token)}><Copy className="w-4 h-4"/></Button>
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Webhook (enviar eventos de acesso) — POST</Label>
                <div className="flex gap-2 mt-1">
                  <Input readOnly value={`${BACKEND}/api/devices/webhook/${tokenDialog.token}`} className="font-mono text-xs"/>
                  <Button size="icon" variant="outline" onClick={()=>copy(`${BACKEND}/api/devices/webhook/${tokenDialog.token}`)}><Copy className="w-4 h-4"/></Button>
                </div>
                <div className="mt-2 p-3 bg-slate-50 rounded-lg text-xs font-mono overflow-x-auto">
                  <div className="text-slate-500 mb-1">Payload exemplo:</div>
{`{
  "event_type": "access_granted",
  "student_cpf": "12345678900",
  "timestamp": "2026-02-11T14:30:00Z"
}`}
                </div>
                {tokenDialog.device.device_type === "reader" && tokenDialog.device.paired_device_id && (
                  <p className="text-xs text-emerald-700 mt-2">
                    ✅ Vinculado à catraca "{deviceName(tokenDialog.device.paired_device_id)}" — ao receber access_granted, a catraca abre automaticamente.
                  </p>
                )}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Sync de rostos (baixar cadastros) — GET</Label>
                <div className="flex gap-2 mt-1">
                  <Input readOnly value={`${BACKEND}/api/devices/sync/students/${tokenDialog.token}`} className="font-mono text-xs"/>
                  <Button size="icon" variant="outline" onClick={()=>copy(`${BACKEND}/api/devices/sync/students/${tokenDialog.token}`)}><Copy className="w-4 h-4"/></Button>
                </div>
              </div>
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-900">
                <strong>Boas práticas:</strong> nunca compartilhe este token. Gire sempre que suspeitar de vazamento.
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ============================================================ */
/* Status Ao Vivo — tiles & helpers                             */
/* ============================================================ */

function StatCard({ label, value, icon: Icon, color = "slate", testid }) {
  const palette = {
    emerald: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    rose: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    amber: "bg-amber-500/15 text-amber-300 border-amber-500/30",
    slate: "bg-white/5 text-white border-white/10",
  }[color] || "bg-white/5 text-white border-white/10";
  return (
    <div className={`rounded-xl border px-4 py-3 ${palette}`} data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest opacity-80">
        <Icon className="w-3.5 h-3.5"/>{label}
      </div>
      <div className="text-3xl font-black font-heading mt-1">{value}</div>
    </div>
  );
}

function DeviceStatusTile({ d }) {
  const isOnline = !!d.online;
  const isTurnstile = d.device_type === "turnstile";
  const neverSeen = !d.last_seen;
  const dotColor = isOnline
    ? "bg-emerald-400"
    : neverSeen
      ? "bg-slate-500"
      : "bg-rose-500";
  const label = isOnline ? "ONLINE" : (neverSeen ? "NUNCA VISTO" : "OFFLINE");
  const labelColor = isOnline ? "text-emerald-300" : neverSeen ? "text-slate-400" : "text-rose-300";
  const since = d.offline_seconds != null ? formatOffline(d.offline_seconds) : null;
  return (
    <div className="rounded-lg bg-slate-900/60 border border-white/10 p-3" data-testid={`status-tile-${d.id}`}>
      <div className="flex items-center gap-2">
        <div className={`w-2.5 h-2.5 rounded-full ${dotColor} ${isOnline ? "animate-pulse" : ""}`}/>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{d.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-400 truncate">
            {isTurnstile ? <DoorOpen className="inline w-3 h-3 mr-1"/> : <ScanFace className="inline w-3 h-3 mr-1"/>}
            {d.model || d.brand}
          </div>
        </div>
        <div className={`text-[10px] font-bold uppercase ${labelColor}`}>{label}</div>
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-slate-400">
        <span>{isOnline ? "ativo agora" : since ? `há ${since}` : "sem heartbeat"}</span>
        {d.pending_sync_count > 0 && (
          <span className="text-amber-300 font-semibold flex items-center gap-1" title="Logs ainda não enviados">
            <WifiOff className="w-3 h-3"/>{d.pending_sync_count}
          </span>
        )}
        {isTurnstile && d.turnstile_latency_ms > 0 && isOnline && (
          <span className="text-emerald-300 font-mono">{d.turnstile_latency_ms}ms</span>
        )}
      </div>
    </div>
  );
}

function formatOffline(secs) {
  if (secs < 60) return `${secs}s`;
  if (secs < 3600) return `${Math.floor(secs/60)}min`;
  if (secs < 86400) return `${Math.floor(secs/3600)}h`;
  return `${Math.floor(secs/86400)}d`;
}

function renderDeviceRow(d, { TYPE_LABELS, BRAND_LABELS, deviceName, testRelease, setTokenDialog, openEdit, rotate, remove, agentCredential }) {
  return (
    <tr key={d.id} className="border-b border-slate-100">
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-rose-50 flex items-center justify-center">
            {d.device_type === "turnstile" ? <DoorOpen className="w-5 h-5 text-emerald-600"/> : <Cpu className="w-5 h-5 text-rose-600"/>}
          </div>
          <div>
            <div className="font-medium text-slate-900">{d.name}</div>
            <div className="text-xs text-slate-500">{d.model || "—"}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3 text-slate-600 text-sm">
        <div>{TYPE_LABELS[d.device_type || "legacy"]}</div>
        <div className="text-xs text-slate-500">{BRAND_LABELS[d.brand]}</div>
      </td>
      <td className="px-4 py-3 text-sm">
        <div className="font-mono text-xs">{d.ip_address || "—"}</div>
      </td>
      <td className="px-4 py-3 text-xs">
        {d.device_type === "reader" && d.paired_device_id && (
          <div className="inline-flex items-center gap-1 text-emerald-700"><Link2 className="w-3 h-3"/>{deviceName(d.paired_device_id)}</div>
        )}
        {d.device_type === "turnstile" && (
          <>
            <div className="font-mono text-slate-700">{d.release_protocol}</div>
            {d.release_protocol === "topdata_tcp" && <div className="text-slate-500">porta {d.release_port}</div>}
            {d.release_protocol === "http" && <div className="text-slate-500 truncate max-w-[180px]">{d.release_endpoint}</div>}
          </>
        )}
        {d.device_type === "legacy" && <span className="text-slate-400">—</span>}
      </td>
      <td className="px-4 py-3 text-xs text-slate-600">{d.last_seen ? new Date(d.last_seen).toLocaleString("pt-BR") : "nunca"}</td>
      <td className="px-4 py-3">
        <span className={`text-xs px-2 py-1 rounded-full font-semibold flex items-center gap-1 w-fit ${d.active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
          <Radio className={`w-3 h-3 ${d.active ? "animate-pulse" : ""}`}/>{d.active ? "Ativo" : "Inativo"}
        </span>
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        {d.unit_id && (
          <Button variant="ghost" size="sm" onClick={()=>agentCredential(d)} data-testid={`device-agent-credential-${d.id}`}>Credencial</Button>
        )}
        {d.device_type === "turnstile" && d.release_protocol !== "agent_easyinner" && (
          <Button variant="ghost" size="sm" onClick={()=>testRelease(d)} data-testid={`device-test-release-${d.id}`} className="text-emerald-700 hover:bg-emerald-50">
            <DoorOpen className="w-3.5 h-3.5 mr-1"/>Testar
          </Button>
        )}
        <Button variant="ghost" size="sm" onClick={()=>setTokenDialog({ token: d.api_token, device: d })} data-testid={`device-token-button-${d.id}`}>URLs</Button>
        <Button variant="ghost" size="icon" onClick={()=>openEdit(d)} data-testid={`device-edit-${d.id}`}><Pencil className="w-3.5 h-3.5 text-slate-500"/></Button>
        <Button variant="ghost" size="icon" onClick={()=>rotate(d.id)}><RotateCcw className="w-3.5 h-3.5"/></Button>
        <Button variant="ghost" size="icon" onClick={()=>remove(d.id)}><Trash2 className="w-4 h-4 text-slate-400"/></Button>
      </td>
    </tr>
  );
}
